import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { CommonActions } from '@react-navigation/native';

import { navigationRef } from '../navigation/navigationRef';
import { useActiveSession } from '../context/ActiveSessionContext';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight } from '../utils/theme';

/**
 * ReturnBanner — the global "get back to your running session" strip.
 *
 * Rendered ONCE at root level (App.tsx, above the NavigationContainer) so it
 * is visible on every screen. Data comes from the shared
 * ActiveSessionContext poller — no fetch of its own.
 *
 * Hidden on:
 *  - auth/welcome screens (index, login, register, …) and public
 *    signup/pay pages — unauthenticated surfaces;
 *  - the active session's own wash screen (nothing to return to);
 *  - the customer home tab (user/index), which renders its richer
 *    active-session card from the same context (avoids double banners).
 */
const HIDDEN_ROUTES = new Set([
  'index',
  'login',
  'register',
  'forgot-password',
  'reset-password',
  'verify-email',
  'verify-2fa',
  'signup/fleet/[accountNumber]',
  'pay/fleet-invoice/[id]',
  'sub-owner-invite/[token]',
  'customer-invite/[token]', // 1.4 P25
  'not-found',
  'user/index', // home tab has its own active-session card
]);

const WASH_ROUTE = 'wash/[locationId]/[bayId]';

export default function ReturnBanner() {
  const { session } = useActiveSession();
  const { colors } = useTheme();
  // Bump on every navigation state change so getCurrentRoute() is re-read.
  const [, setNavTick] = useState(0);

  useEffect(() => {
    const unsub = navigationRef.addListener('state', () => setNavTick(t => t + 1));
    return unsub;
  }, []);

  if (!session?.active || !session.locationId || !session.bayId) return null;
  if (!navigationRef.isReady()) return null;
  const route = navigationRef.getCurrentRoute();
  if (!route || HIDDEN_ROUTES.has(route.name)) return null;
  const params = (route.params ?? {}) as { bayId?: string };
  if (route.name === WASH_ROUTE && params.bayId === session.bayId) return null;

  const noun = session.bayType === 'vacuum' ? 'Vacuum' : 'Wash';
  const where = [session.locationName, session.bayName].filter(Boolean).join(' — ');

  return (
    <TouchableOpacity
      onPress={() => {
        navigationRef.dispatch(CommonActions.navigate({
          name: WASH_ROUTE,
          params: { locationId: session.locationId, bayId: session.bayId },
        }));
      }}
      accessibilityRole="button"
      accessibilityLabel={`${noun} in progress — tap to return`}
      style={[styles.bar, { backgroundColor: colors.success + '14', borderBottomColor: colors.success + '40' }]}
    >
      <View style={[styles.pulse, { backgroundColor: colors.success }]} />
      <View style={styles.info}>
        <Text style={[styles.title, { color: colors.success }]} numberOfLines={1}>
          {noun} in Progress — tap to return
        </Text>
        {where ? (
          <Text style={[styles.sub, { color: colors.textSecondary }]} numberOfLines={1}>{where}</Text>
        ) : null}
      </View>
      <Ionicons name="chevron-forward" size={18} color={colors.success} />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderBottomWidth: 1,
  },
  pulse: { width: 10, height: 10, borderRadius: 5 },
  info: { flex: 1 },
  title: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
  sub: { fontSize: fontSize.xs, marginTop: 1 },
});
